import { observer } from 'mobx-react-lite';
import { type ComponentProps, useEffect, useState } from 'react';

import { useAuthStore } from '../../stores/authStore';
import { NAVINA_EMAIL_KEY } from '../../utils/consts';
import {
	embedZendeskScriptOnce,
	getZendesk,
	Positions,
	TicketForms,
	ZendeskModel,
	type ZendeskSettings,
	type Zendesk,
} from './ZendeskModel';

interface SubmitFeedbackGlobalFeatureProps extends ComponentProps<'div'> {
	readonly customFields?: ZendeskSettings['customFields'];
}

// If the browser never goes idle we still want the widget to eventually embed, so the idle callback is
// given a timeout after which it fires regardless.
const ZENDESK_EMBED_IDLE_TIMEOUT_IN_MS = 3_000;

// The Zendesk widget (snippet.js and the heavy web-widget bundle) must never compete with Navina's
// critical initial load, so its injection is deferred until the browser is idle. The button renders
// null until the widget is ready, so a slightly later load is invisible to the user.
function scheduleZendeskScriptEmbedWhenIdle(): () => void {
	if (typeof globalThis.requestIdleCallback === 'function') {
		const idleCallbackHandle = globalThis.requestIdleCallback(
			function embedZendeskScriptWhenIdle(): void {
				embedZendeskScriptOnce();
			},
			{ timeout: ZENDESK_EMBED_IDLE_TIMEOUT_IN_MS },
		);

		return function cancelIdleCallback(): void {
			globalThis.cancelIdleCallback(idleCallbackHandle);
		};
	}

	const timeoutHandle = globalThis.setTimeout(function embedZendeskScriptOnNextTick(): void {
		embedZendeskScriptOnce();
	}, 0);

	return function clearTimeoutHandle(): void {
		globalThis.clearTimeout(timeoutHandle);
	};
}

export const SubmitFeedbackGlobalFeature = observer(function SubmitFeedbackGlobalFeature({
	children,
	customFields: outerCustomFields = [] as const,
	...props
}: SubmitFeedbackGlobalFeatureProps) {
	const authStore = useAuthStore();
	const { userFullName } = authStore;
	const userEmail = localStorage.getItem(NAVINA_EMAIL_KEY) || authStore.getEmail;

	const [zendesk, setZendesk] = useState<{ readonly instance: Zendesk } | null>(null);
	const zendeskInstance = zendesk && zendesk.instance;

	useEffect(function getZendeskInstance() {
		getZendesk().then(function updateZendeskInstance(instance): void {
			setZendesk({ instance });
		});
	}, []);

	useEffect(function loadZendeskScript(): () => void {
		return scheduleZendeskScriptEmbedWhenIdle();
	}, []);

	if (!zendeskInstance) {
		return null;
	}

	const customFields = [
		{ id: 'name', defaultValue: userFullName },
		{ id: 'email', defaultValue: userEmail },
		...outerCustomFields,
	] as const satisfies ZendeskSettings['customFields'];

	const zendeskModel = ZendeskModel.create({ zendeskInstance: zendeskInstance });
	zendeskModel.initialize(
		{
			horizontalPosition: Positions.Right,
			verticalPosition: Positions.Bottom,
			offsetX: 0,
			offsetY: 0,
			formTitle: 'Contact Us',
			attachments: true,
			ticketForms: [TicketForms.ContactUs],
			customFields,
		} as const satisfies ZendeskSettings,
		Object.values(customFields)
			.filter(function isFieldWithDefaultValue(field): boolean {
				return Boolean(field.defaultValue);
			})
			.map(function getCustomFieldId(field): string {
				return field.id;
			}),
	);

	return (
		<div
			{...{
				onClick: zendeskModel.open,
				...props,
			}}
		>
			{children}
		</div>
	);
});
